// Adding a hook/forwardRef/context to a wrapper? Add 'use client' with it —
// pure wrappers are server-importable and rely on Mantine's own directives.

export { Accordion, AccordionItem, AccordionControl, AccordionPanel } from './accordion';
export { Alert, type AlertProps } from './alert';
export { Autocomplete, type AutocompleteProps } from './autocomplete';
export { Avatar, type AvatarGroupProps, type AvatarProps } from './avatar';

export { Badge, type BadgeProps } from './badge';
export { Breadcrumbs, type BreadcrumbsProps } from './breadcrumbs';
export { Box } from './box';
export {
  Checkbox,
  type CheckboxCardProps,
  type CheckboxGroupProps,
  type CheckboxProps,
} from './checkbox';
export {
  ActionIcon,
  type ActionIconProps,
  Button,
  type ButtonProps,
  type ButtonVariant,
  CopyButton,
  FileButton,
  UnstyledButton,
} from './button';

export { Card, type CardProps } from './card';
export { Chip, type ChipGroupProps, type ChipProps } from './chip';
export { CloseButton, type CloseButtonProps } from './close-button';
export { Code } from './code';
export { Collapse } from './collapse';
export { ColorPicker, type ColorPickerProps } from './color-picker';
export { ColorSwatch, type ColorSwatchProps } from './color-swatch';
export type { DefaultColor } from './colors';

export { Droppable } from './droppable';

export { Divider } from './divider';
export { Drawer, type DrawerProps } from './drawer';
export {
  ErrorBoundary,
  withErrorBoundary,
  ErrorReportingProvider,
  useErrorReporting,
  type ErrorBoundaryOnError,
  type ErrorReportingHandler,
} from './error-boundary';

export { Fieldset, type FieldsetProps } from './fieldset';

export { Highlight, type HighlightProps } from './highlight';
export {
  useTheme,
  useConfirmModal,
  type HandleConfirmProps,
  useConfirmDeleteModal,
  type HandleConfirmDeleteProps,
  useWarnBeforeLeave,
} from './hooks';

export { Image, type ImageProps } from './image';
export {
  DateInput,
  DateRangeInput,
  type DateRange,
  InputBase,
  type InputBaseProps,
  JsonInput,
  type JsonInputProps,
  MultiSelect,
  type MultiSelectProps,
  PasswordInput,
  PinInput,
  Slider,
  Switch,
} from './input';
export { TextInput, type TextInputProps } from './text-input';
export { FileInput, type FileInputProps } from './file-input';
export { NumberInput, type NumberInputProps } from './number-input';
export { Select, type SelectProps } from './select';
// Creatable/transition plumbing for the CreatableSelect/CreatableMultiSelect/InlineSelect
// compositions, which re-implement the v6-compat creatable flow on top of these wrappers.
// Composition-internal — don't build new general-purpose call sites on these.
export {
  CREATE_OPTION_VALUE,
  resolveCreatedValue,
  withCreateOption,
  type CreatableSelectProps,
  SELECT_TRANSITION_PROPS,
} from './select';
export type { ComboboxData, ComboboxItem, MantineSize } from '@mantine/core';

export {
  Center,
  Container,
  Flex,
  Grid,
  Group,
  type GroupProps,
  SimpleGrid,
  Space,
  Stack,
  type StackProps,
} from './layout';
export { HoverCard, type HoverCardProps } from './hover-card';
export { Link, type LinkProps } from './link';
export { List } from './list';
export { Loader } from './loader';

export { Modal, ModalFooter, type ModalProps, ModalsProviderWrapper } from './modal';
export { Menu, type MenuProps } from './menu';

export { NavLink, type NavLinkProps } from './nav-link';
export {
  showNotification,
  showInfoNotification,
  showWarningNotification,
  showSuccessNotification,
  showErrorNotification,
  type NotificationProps,
} from './notification';

export { DisabledOverlay, LoadingOverlay, type LoadingOverlayProps, Overlay } from './overlay';

export { Paper, type PaperProps } from './paper';
export { Pill, type PillProps } from './pill';
export { Popover, type PopoverProps } from './popover';
export { Progress } from './progress';

export { Radio, type RadioCardProps, type RadioGroupProps, type RadioProps } from './radio';
export { RichTextEditor, type RichTextEditorProps } from './rich-text-editor';
export { RingProgress } from './ring-progress';

export { SafeHtmlRenderer } from './safe-html-renderer';
export { ScrollArea } from './scroll-area';
export { ScrollableCard } from './scrollable-card';
export { Scroller } from './scroller';
export { SegmentedControl } from './segmented-control';
export { Skeleton, type SkeletonProps } from './skeleton';
export { Stepper, type StepperProps, type StepperStepProps } from './stepper';

export {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  type TableHeadProps,
  TableHeader,
  TableRow,
} from './table';
export { Tabs, TabsList, TabsTab, TabsPanel, type TabsWizardStatus } from './tabs';
export { Text, type TextProps } from './text';
export { TextArea, type TextAreaProps } from './text-area';
export { ThemeIcon } from './theme-icon';
export { Title, type TitleProps } from './title';
export { Transition } from './transition';
export { Tooltip, FloatingPortalRootContext, type TooltipProps } from './tooltip';
export { Typography, type TypographyProps } from './typography';
export type { LockedProps } from './types';
export { VisuallyHidden, type VisuallyHiddenProps } from './visually-hidden';
